// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — the "Cite this view" dialog (`docs/AYNI_XR_RESEARCH_TIER_PLAN.md`,
 * slice 1). Opened from the info panel's data credit and from the
 * Analyze panel, on every plan: a citation is how a CC BY source's
 * licence is honoured, and every pasted link brings a reader back.
 *
 * One citation in three forms (text, BibTeX, RIS) behind tabs, each
 * copied or downloaded, and the link that reopens the view. The forms
 * are built by `src/services/research/citation.ts`; this module only
 * shows them.
 */

import {
  citationBibtex,
  citationKey,
  citationRis,
  citationText,
  formatFrameTime,
  type CitationInput,
} from '../services/research/citation'
import { t } from '../i18n'
import { trail, trailFile } from '../services/research/researchTrail'
import { escapeAttr, escapeHtml } from './domUtils'

export type CitationFormat = 'text' | 'bibtex' | 'ris'

const FORMATS: readonly CitationFormat[] = ['text', 'bibtex', 'ris']
const DIALOG_ID = 'cite-view-dialog'

/** The citation in one form, and the file it downloads as. */
export function citationIn(format: CitationFormat, input: CitationInput): { text: string; filename: string; mime: string } {
  const key = citationKey(input)
  if (format === 'bibtex') return { text: citationBibtex(input), filename: `${key}.bib`, mime: 'application/x-bibtex' }
  if (format === 'ris') return { text: citationRis(input), filename: `${key}.ris`, mime: 'application/x-research-info-systems' }
  return { text: `${citationText(input)}\n`, filename: `${key}.txt`, mime: 'text/plain' }
}

async function copyText(text: string, fallbackFrom: HTMLTextAreaElement | HTMLInputElement): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text)
    return true
  } catch {
    // No clipboard permission (an embed, an old browser): select the
    // field and use the legacy command, which still works on a click.
    fallbackFrom.focus()
    fallbackFrom.select()
    try {
      return document.execCommand('copy')
    } catch {
      return false
    }
  }
}

function download(filename: string, text: string, mime: string): void {
  if (typeof URL.createObjectURL !== 'function') return
  // AYNI: a research record, when one is recording, keeps the file's name and SHA-256 (slice 4).
  trailFile({ name: filename, body: text, kind: 'citation' })
  const url = URL.createObjectURL(new Blob([text], { type: `${mime};charset=utf-8` }))
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}

export function isCiteViewDialogOpen(): boolean {
  return !!document.getElementById(DIALOG_ID)
}

/** Close without giving focus back (a replacement dialog, a test); its key listener goes on the next key. */
export function closeCiteViewDialog(): void {
  document.getElementById(DIALOG_ID)?.remove()
}

/** Open the dialog for one view; replaces any dialog already open. */
export function openCiteViewDialog(
  input: CitationInput,
  opts: { trigger?: HTMLElement | null; announce?: (message: string) => void } = {},
): void {
  closeCiteViewDialog()
  let format: CitationFormat = 'text'
  const backdrop = document.createElement('div')
  backdrop.id = DIALOG_ID
  backdrop.className = 'cite-backdrop'
  const frame = input.frameMs !== null
    ? `<p class="cite-sub">${escapeHtml(t('cite.frame', { time: formatFrameTime(input.frameMs, input.framePrecision) }))}</p>`
    : ''
  backdrop.innerHTML = `
    <div class="cite-dialog" role="dialog" aria-modal="true" aria-labelledby="cite-title">
      <h2 class="cite-title" id="cite-title">${escapeHtml(t('cite.title'))}</h2>
      <p class="cite-dataset">${escapeHtml(input.title)}</p>
      ${frame}
      <div class="cite-tabs" role="tablist" aria-label="${escapeAttr(t('cite.formats'))}">
        ${FORMATS.map((f) => `<button type="button" role="tab" class="cite-tab" id="cite-tab-${f}" data-format="${f}" aria-controls="cite-output">${escapeHtml(t(`cite.format.${f}`))}</button>`).join('')}
      </div>
      <textarea id="cite-output" class="cite-output" role="tabpanel" readonly rows="7" spellcheck="false"></textarea>
      <div class="cite-actions">
        <span class="cite-status" role="status" aria-live="polite"></span>
        <button type="button" class="cite-download">${escapeHtml(t('cite.download'))}</button>
        <button type="button" class="cite-copy">${escapeHtml(t('cite.copy'))}</button>
      </div>
      <label class="cite-link-label" for="cite-link">${escapeHtml(t('cite.link'))}</label>
      <div class="cite-link-row">
        <input id="cite-link" class="cite-link" type="text" readonly value="${escapeAttr(input.viewUrl)}">
        <button type="button" class="cite-copy-link">${escapeHtml(t('cite.copyLink'))}</button>
      </div>
      <p class="cite-note">${escapeHtml(t('cite.note'))}</p>
      <div class="cite-footer">
        <button type="button" class="cite-close">${escapeHtml(t('cite.close'))}</button>
      </div>
    </div>`
  document.body.appendChild(backdrop)

  const dialog = backdrop.querySelector<HTMLElement>('.cite-dialog')!
  const output = backdrop.querySelector<HTMLTextAreaElement>('#cite-output')!
  const status = backdrop.querySelector<HTMLElement>('.cite-status')!
  const link = backdrop.querySelector<HTMLInputElement>('#cite-link')!
  const tabs = [...backdrop.querySelectorAll<HTMLButtonElement>('.cite-tab')]

  const say = (message: string) => {
    status.textContent = message
    opts.announce?.(message)
  }
  const show = (next: CitationFormat) => {
    format = next
    output.value = citationIn(format, input).text
    output.classList.toggle('cite-output-code', format !== 'text')
    output.setAttribute('aria-labelledby', `cite-tab-${format}`)
    for (const tab of tabs) {
      const on = tab.dataset.format === format
      tab.setAttribute('aria-selected', String(on))
      tab.tabIndex = on ? 0 : -1
    }
    status.textContent = ''
  }
  const finish = () => {
    window.removeEventListener('keydown', onKey, true)
    backdrop.remove()
    opts.trigger?.focus()
  }

  for (const tab of tabs) tab.addEventListener('click', () => show(tab.dataset.format as CitationFormat))
  backdrop.querySelector('.cite-tabs')!.addEventListener('keydown', (e) => {
    const key = (e as KeyboardEvent).key
    if (key !== 'ArrowRight' && key !== 'ArrowLeft') return
    e.preventDefault()
    const step = (key === 'ArrowRight') === (document.dir !== 'rtl') ? 1 : -1
    const next = FORMATS[(FORMATS.indexOf(format) + step + FORMATS.length) % FORMATS.length]
    show(next)
    tabs[FORMATS.indexOf(next)].focus()
  })
  backdrop.querySelector('.cite-copy')!.addEventListener('click', async () => {
    // The built text, not the field's: a textarea turns RIS's CRLF into LF.
    const copied = await copyText(citationIn(format, input).text, output)
    say(t(copied ? 'cite.copied' : 'cite.copyFailed'))
    // AYNI: a research record, when one is recording, notes the citation taken (slice 4).
    if (copied) trail({ type: 'cite', data: { action: 'copied', format, citation: citationText(input), viewUrl: input.viewUrl, summary: `Citation copied (${format})` }, frame: true })
  })
  backdrop.querySelector('.cite-copy-link')!.addEventListener('click', async () => {
    const copied = await copyText(input.viewUrl, link)
    say(t(copied ? 'cite.linkCopied' : 'cite.copyFailed'))
    if (copied) trail({ type: 'cite', data: { action: 'link copied', viewUrl: input.viewUrl, summary: 'View link copied' }, frame: true })
  })
  backdrop.querySelector('.cite-download')!.addEventListener('click', () => {
    const file = citationIn(format, input)
    download(file.filename, file.text, file.mime)
    say(t('cite.downloaded', { file: file.filename }))
  })
  backdrop.querySelector('.cite-close')!.addEventListener('click', finish)
  backdrop.addEventListener('pointerdown', (e) => {
    if (e.target === backdrop) finish()
  })
  // On the window, in the capture phase: the dialog is modal, so its
  // Escape and Tab come before any panel's own (the Analyze panel closes
  // itself on Escape from a capture listener on the document).
  function onKey(e: KeyboardEvent): void {
    if (!backdrop.isConnected) {
      window.removeEventListener('keydown', onKey, true)
      return
    }
    if (e.key === 'Escape') {
      e.preventDefault()
      e.stopPropagation()
      finish()
    } else if (e.key === 'Tab') {
      // Keep focus inside the dialog; the tab list is one stop.
      const stops = [...dialog.querySelectorAll<HTMLElement>('[role="tab"][aria-selected="true"], textarea, input, .cite-actions button, .cite-link-row button, .cite-footer button')]
      const i = stops.indexOf(document.activeElement as HTMLElement)
      const next = e.shiftKey ? (i <= 0 ? stops.length - 1 : i - 1) : (i + 1) % stops.length
      e.preventDefault()
      stops[next].focus()
    }
  }
  window.addEventListener('keydown', onKey, true)

  show('text')
  tabs[0].focus()
}
